import {
  DocsCallout,
  DocsContent,
  DocsLayout,
  DocsScreenshot,
  DocsStepList,
  H2,
  H3,
} from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsDocumentProcessing() {
  return (
    <DocsLayout>
      <DocsContent
        title="Document Processing"
        description="Learn how to upload documents and understand XENDREL's AI-powered extraction pipelines that transform your files into structured, queryable data."
        previousPage={{ title: 'Case Management', href: '/docs/case-management' }}
        nextPage={{ title: 'Phase 2 Pipelines', href: '/docs/pipelines' }}
      >
        <H2 id="supported-document-types">Supported Document Types</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          XENDREL can process a variety of document formats, each with specialized extraction
          capabilities:
        </p>

        <div className="space-y-4 my-6">
          <DocumentTypeCard
            type="PDF"
            extensions=".pdf"
            description="Standard document format. XENDREL extracts text content and processes it for entities. Scanned PDFs with OCR are also supported."
          />
          <DocumentTypeCard
            type="Word Documents"
            extensions=".docx"
            description="Microsoft Word documents. Text, tables, and structured content are extracted while preserving context."
          />
          <DocumentTypeCard
            type="Spreadsheets"
            extensions=".csv, .xlsx"
            description="Tabular data files. Each row is processed for entity extraction, making it ideal for transaction records, contact lists, and databases."
          />
          <DocumentTypeCard
            type="Identity Documents"
            extensions=".jpg, .png, .pdf"
            description="Passports, driver's licenses, and ID cards. Specialized OCR and field extraction for identity verification data."
          />
        </div>

        <DocsScreenshot
          src="/docs/images/documents/document-types.svg"
          alt="Supported Document Types"
          caption="Various document types supported by XENDREL"
        />

        <H2 id="uploading-documents">Uploading Documents</H2>

        <DocsStepList
          steps={[
            {
              title: 'Open the Documents Panel',
              description: (
                <>
                  From your case view, click the{' '}
                  <strong className="text-foreground">Documents</strong> button in the top toolbar
                  to open the document management panel.
                </>
              ),
            },
            {
              title: 'Select Files',
              description: (
                <>
                  Click <strong className="text-foreground">Upload Document</strong> to open the
                  file picker, or drag and drop files directly onto the upload area. You can select
                  multiple files at once.
                </>
              ),
            },
            {
              title: 'Configure Upload Options',
              description: (
                <>
                  For each file, you can optionally:
                  <ul className="mt-2 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500">•</span>
                      <span>Rename the document for better organization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500">•</span>
                      <span>Add tags or categories</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500">•</span>
                      <span>Select a specific extraction pipeline</span>
                    </li>
                  </ul>
                </>
              ),
            },
            {
              title: 'Start Processing',
              description: (
                <>
                  Click <strong className="text-foreground">Upload</strong> to begin. Files are
                  uploaded and queued for processing automatically.
                </>
              ),
            },
          ]}
        />

        <DocsScreenshot
          src="/docs/images/documents/upload-wizard.svg"
          alt="Document Upload Interface"
          caption="The document upload wizard with pipeline selection"
        />

        <H2 id="extraction-pipelines">Extraction Pipelines</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Pipelines are specialized AI configurations optimized for different document types.
          Choosing the right pipeline improves extraction accuracy.
        </p>

        <H3 id="standard-pipeline">Standard Pipeline</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          The default pipeline for general documents. It extracts:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">People</strong> - Names, aliases, and identifying
              information
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Organizations</strong> - Companies, agencies, and
              groups
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Locations</strong> - Addresses, cities, countries
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Dates</strong> - Events, meetings, deadlines
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Contact info</strong> - Emails, phone numbers,
              social handles
            </span>
          </li>
        </ul>

        <H3 id="financial-pipeline">Financial Pipeline</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Optimized for bank statements, invoices, and financial records. Additional extraction
          includes:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Transactions</strong> - Amounts, dates,
              counterparties
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Account numbers</strong> - Bank accounts, routing
              numbers
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Monetary values</strong> - Properly parsed
              currency amounts
            </span>
          </li>
        </ul>

        <H3 id="identity-pipeline">Identity Document Pipeline</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Specialized for passports, driver's licenses, and ID cards. Extracts:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Personal details</strong> - Full name, date of
              birth, nationality
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Document info</strong> - ID number, issue/expiry
              dates, issuing authority
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Biometric data</strong> - Photo extraction for
              visual reference
            </span>
          </li>
        </ul>

        <DocsCallout variant="tip" title="Pipeline Selection">
          Not sure which pipeline to use? Start with the Standard pipeline - it works well for most
          documents. You can always reprocess a document with a different pipeline later.
        </DocsCallout>

        <H2 id="processing-status">Processing Status</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Track your documents through the processing lifecycle:
        </p>

        <div className="space-y-3 my-6">
          <ProcessingStatus
            status="Pending"
            description="Document is queued and waiting to be processed"
          />
          <ProcessingStatus
            status="Processing"
            description="AI is actively extracting entities from the document"
          />
          <ProcessingStatus
            status="Completed"
            description="Processing finished successfully. Entities are in the graph"
          />
          <ProcessingStatus
            status="Failed"
            description="An error occurred. Check the error message for details"
          />
        </div>

        <DocsCallout variant="info" title="Processing Time">
          Processing time depends on document size and complexity. A typical 10-page PDF takes 30-60
          seconds. Large documents or those with complex tables may take longer.
        </DocsCallout>

        <H2 id="viewing-extracted-data">Viewing Extracted Data</H2>

        <H3 id="document-entities">Document Entities</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          After processing, click on a document to see all entities extracted from it. Each entity
          shows:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Entity type (Person, Organization, Location, etc.)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Extracted name or identifier</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>All properties found (addresses, dates, amounts, etc.)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Confidence score for the extraction</span>
          </li>
        </ul>

        <H3 id="source-highlighting">Source Highlighting</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Click on any extracted entity to see where in the original document it was found. This
          helps verify extractions and understand context.
        </p>

        <H2 id="reprocessing-documents">Reprocessing Documents</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          You may want to reprocess a document when:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>The extraction schema has been updated with new entity types</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>You want to try a different pipeline</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>The initial processing encountered errors</span>
          </li>
        </ul>

        <DocsCallout variant="warning" title="Reprocessing Warning">
          Reprocessing removes all entities previously extracted from the document before running
          extraction again. Entity resolutions and manual edits will be lost.
        </DocsCallout>

        <DocsStepList
          steps={[
            {
              title: 'Select the Document',
              description: 'In the Documents panel, find the document you want to reprocess.',
            },
            {
              title: 'Open Actions Menu',
              description: 'Click the three-dot menu on the document card.',
            },
            {
              title: 'Choose Reprocess',
              description: (
                <>
                  Select <strong className="text-foreground">Reprocess</strong> and optionally
                  choose a different pipeline.
                </>
              ),
            },
            {
              title: 'Confirm and Wait',
              description: 'Confirm the action and wait for processing to complete.',
            },
          ]}
        />

        <H2 id="troubleshooting">Troubleshooting</H2>

        <H3 id="upload-failures">Upload Failures</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">File too large</strong> - Maximum file size is
              50MB. Split larger documents.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Unsupported format</strong> - Convert to a
              supported format (PDF, DOCX, CSV).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Network error</strong> - Check your connection and
              try again.
            </span>
          </li>
        </ul>

        <H3 id="processing-failures">Processing Failures</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Corrupt file</strong> - The document may be
              damaged. Try opening it locally first.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Password protected</strong> - Remove password
              protection before uploading.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Scanned with poor quality</strong> - OCR may fail
              on low-resolution scans.
            </span>
          </li>
        </ul>
      </DocsContent>
    </DocsLayout>
  )
}

function DocumentTypeCard({
  type,
  extensions,
  description,
}: {
  type: string
  extensions: string
  description: string
}) {
  return (
    <Card size="compact" className="rounded-lg p-4 bg-card/50">
      <div className="flex items-center gap-3 mb-2">
        <span className="font-semibold text-foreground">{type}</span>
        <span className="text-xs text-muted-foreground font-mono">{extensions}</span>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Card>
  )
}

function ProcessingStatus({ status, description }: { status: string; description: string }) {
  const statusColors: Record<string, string> = {
    Pending: 'text-muted-foreground',
    Processing: 'text-blue-600 dark:text-blue-400',
    Completed: 'text-green-600 dark:text-green-400',
    Failed: 'text-red-600 dark:text-red-400',
  }

  return (
    <div className="flex items-center gap-3">
      <span className={`font-medium w-24 ${statusColors[status]}`}>{status}</span>
      <span className="text-muted-foreground text-sm">{description}</span>
    </div>
  )
}
